import OpenAI from "openai";
import { db } from "@workspace/db";
import { sql } from "drizzle-orm";

let _client: OpenAI | null = null;
let _cachedModel: string | null = null;
let _cacheTs = 0;
const CACHE_TTL = 60_000;

async function getDbSetting(key: string): Promise<string | null> {
  try {
    const rows = await db.execute(sql`SELECT value FROM platform_settings WHERE key = ${key}`);
    return (rows.rows?.[0] as any)?.value ?? null;
  } catch {
    return null;
  }
}

async function getModelSettings(): Promise<{ model: string; apiKey: string | null }> {
  if (Date.now() - _cacheTs < CACHE_TTL && _cachedModel) {
    return { model: _cachedModel, apiKey: null };
  }
  const [model, apiKey] = await Promise.all([
    getDbSetting("ai_model"),
    getDbSetting("ai_api_key"),
  ]);
  _cachedModel = model ?? "gpt-4o";
  _cacheTs = Date.now();
  _client = null; // reset so it re-initialises with correct key
  return { model: _cachedModel, apiKey };
}

export async function getClient(): Promise<{ client: OpenAI; model: string }> {
  const { model, apiKey } = await getModelSettings();
  const key = apiKey ?? process.env.OPENAI_API_KEY;
  if (!key) throw new Error("OPENAI_API_KEY is required. Please set it in your environment secrets.");
  if (!_client) {
    _client = new OpenAI({ apiKey: key });
  }
  return { client: _client, model };
}

export async function analyzeCompleteTender(
  tenderText: string,
  companyProfile: string,
  language: "ar" | "en",
): Promise<Record<string, unknown>> {
  const { client, model } = await getClient();

  const defaultAr = `أنت محلل مناقصات حكومية سعودي خبير. قم بتحليل المناقصة التالية بدقة وموضوعية وأرجع نتيجة التحليل بصيغة JSON فقط بدون أي نص إضافي.`;
  const defaultEn = `You are an expert Saudi government tender analyst. Analyze the following tender accurately and objectively. Return only JSON without any additional text.`;

  const [promptAr, promptEn] = await Promise.all([
    getDbSetting("system_prompt_ar"),
    getDbSetting("system_prompt_en"),
  ]);

  const systemPrompt = language === "ar" ? (promptAr || defaultAr) : (promptEn || defaultEn);

  const userPrompt = `
${language === "ar" ? "ملف الشركة:" : "Company Profile:"}
${companyProfile}

${language === "ar" ? "نص المناقصة:" : "Tender Text:"}
${tenderText.slice(0, 12000)}

${language === "ar" ? "قم بإرجاع JSON التالي:" : "Return the following JSON:"}
{
  "executiveSummary": "string",
  "tenderType": "string",
  "submissionDeadline": "string",
  "requiredDocuments": ["string"],
  "technicalRequirements": ["string"],
  "financialRequirements": ["string"],
  "legalRequirements": ["string"],
  "qualificationRequirements": ["string"],
  "risks": [{"title": "string", "severity": "low|medium|high", "description": "string"}],
  "bidDecision": "bid|no_bid|needs_review",
  "bidScore": 0,
  "bidReasons": ["string"],
  "missingInformation": ["string"],
  "proposalOutline": {}
}`;

  const response = await client.chat.completions.create({
    model,
    messages: [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ],
    response_format: { type: "json_object" },
    temperature: 0.3,
  });

  const content = response.choices[0]?.message?.content;
  if (!content) throw new Error("Empty AI response");
  return JSON.parse(content);
}

/**
 * Summarize a long tender document into a structured, comprehensive brief
 * that preserves all key facts (scope, deliverables, deadlines, eligibility,
 * pricing instructions, evaluation criteria, BoQ items, penalties, etc.).
 * Used to fit huge multi-page tender PDFs into the proposal-generation context.
 */
export async function summarizeTenderDocument(
  fullText: string,
  language: "ar" | "en",
): Promise<string> {
  if (!fullText || fullText.length < 4000) return fullText;
  const { client, model } = await getClient();

  // Chunked map-reduce style summarization to avoid token limits.
  const CHUNK = 18000;
  const chunks: string[] = [];
  for (let i = 0; i < fullText.length; i += CHUNK) chunks.push(fullText.slice(i, i + CHUNK));

  const partialSummaries: string[] = [];
  for (const chunk of chunks) {
    const prompt = language === "ar"
      ? `استخرج من هذا الجزء من وثيقة مناقصة جميع الحقائق التشغيلية المهمة بشكل منظم وموجز قدر الإمكان مع الحفاظ على الأرقام والتواريخ والأسماء كما هي حرفياً. غطّ: نطاق العمل التفصيلي، المخرجات، المواعيد، الشروط المالية، جدول الكميات/الأسعار، معايير التقييم، الضمانات، الغرامات، شروط الأهلية، الوثائق المطلوبة. لا تكتب مقدمات.\n\n${chunk}`
      : `Extract from this tender-document chunk all operationally important facts in a tight, structured form, preserving numbers, dates, and names verbatim. Cover: detailed scope, deliverables, deadlines, financial conditions, BoQ/pricing schedule, evaluation criteria, guarantees, penalties, eligibility, required documents. No preamble.\n\n${chunk}`;
    const resp = await client.chat.completions.create({
      model,
      messages: [{ role: "user", content: prompt }],
      temperature: 0.1,
      max_tokens: 2000,
    });
    const out = resp.choices[0]?.message?.content?.trim() ?? "";
    if (out) partialSummaries.push(out);
  }

  if (partialSummaries.length === 1) return partialSummaries[0];

  const reducePrompt = language === "ar"
    ? `لديك ملخصات جزئية متعاقبة من وثيقة مناقصة واحدة. ادمجها في موجز شامل واحد منظّم بعناوين واضحة (نطاق العمل، المخرجات، المواعيد، التقييم، المالية، الضمانات، الأهلية، الوثائق) مع الحفاظ على الأرقام والتواريخ كما هي.\n\n${partialSummaries.join("\n\n---\n\n")}`
    : `You have sequential partial summaries from a single tender document. Merge them into one comprehensive, well-organized brief with clear headings (Scope, Deliverables, Deadlines, Evaluation, Financial, Guarantees, Eligibility, Documents). Preserve numbers and dates verbatim.\n\n${partialSummaries.join("\n\n---\n\n")}`;
  const final = await client.chat.completions.create({
    model,
    messages: [{ role: "user", content: reducePrompt }],
    temperature: 0.1,
    max_tokens: 3500,
  });
  return final.choices[0]?.message?.content?.trim() ?? partialSummaries.join("\n\n");
}

export async function generateProposal(
  tenderText: string,
  analysis: Record<string, unknown>,
  companyProfile: string,
  language: "ar" | "en",
  extractedDocText?: string,
  aiNotes?: string,
): Promise<Record<string, string>> {
  const { client, model } = await getClient();

  const defaultAr = `أنت خبير متخصص في كتابة العروض الفنية والمالية للمناقصات الحكومية السعودية وفق لوائح هيئة المشتريات الحكومية. اكتب عرضاً شاملاً ومتكاملاً باللغة العربية بأسلوب احترافي مقنع.`;
  const defaultEn = `You are an expert in writing comprehensive technical and financial proposals for Saudi government tenders. Write a professional, persuasive proposal in English following Saudi procurement standards.`;

  const [promptAr, promptEn] = await Promise.all([
    getDbSetting("proposal_prompt_ar"),
    getDbSetting("proposal_prompt_en"),
  ]);

  const systemPrompt = language === "ar" ? (promptAr || defaultAr) : (promptEn || defaultEn);

  const docContext = extractedDocText
    ? `\n${language === "ar" ? "ملخص شامل لوثائق المناقصة (مستخرج من كامل الملفات):" : "Comprehensive Tender Document Brief (extracted from full files):"}\n${extractedDocText.slice(0, 14000)}\n`
    : "";

  const notesContext = aiNotes?.trim()
    ? `\n${language === "ar" ? "تعليمات خاصة / ملاحظات العميل:" : "Special Instructions / Client Notes:"}\n${aiNotes.trim()}\n`
    : "";

  const userPrompt = `
${language === "ar" ? "ملف الشركة:" : "Company Profile:"}
${companyProfile}
${docContext}
${language === "ar" ? "نتائج تحليل المناقصة:" : "Tender Analysis Results:"}
${JSON.stringify(analysis).slice(0, 2500)}
${notesContext}
${language === "ar"
  ? "اكتب عرضاً فنياً ومالياً متكاملاً واحترافياً يشمل جميع الأقسام التالية. يجب أن يكون كل قسم مفصلاً وموجهاً تحديداً لهذه المناقصة بناءً على وثائقها ومتطلباتها. أرجع النتيجة بصيغة JSON فقط:"
  : "Write a comprehensive and professional technical and financial proposal covering all sections below. Each section must be detailed and specifically tailored to this tender based on its documents and requirements. Return JSON only:"}
{
  "introduction": "string — comprehensive introduction addressing this specific tender",
  "scopeUnderstanding": "string — detailed understanding of scope from tender documents",
  "methodology": "string — step-by-step implementation methodology",
  "team": "string — team structure, roles, qualifications and experience",
  "timeline": "string — detailed project timeline with phases and milestones",
  "qualityManagement": "string — quality assurance plan and standards",
  "riskManagement": "string — risk identification, assessment and mitigation",
  "conclusion": "string — compelling closing statement",
  "financialSummary": "string — executive financial summary",
  "pricingTable": "string — detailed pricing breakdown with line items",
  "paymentTerms": "string — payment schedule and terms",
  "financialGuarantees": "string — performance bonds and guarantees",
  "totalAmount": "string — total contract value with justification"
}`;

  const response = await client.chat.completions.create({
    model,
    messages: [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ],
    response_format: { type: "json_object" },
    temperature: 0.45,
    max_tokens: 6000,
  });

  const content = response.choices[0]?.message?.content;
  if (!content) throw new Error("Empty AI response");
  return JSON.parse(content);
}

const SECTION_DESCRIPTIONS_AR: Record<string, string> = {
  introduction: "مقدمة احترافية موجهة لهذه المناقصة بالذات: تعرّف بالشركة وبسبب اهتمامها بالمشروع وقيمتها المضافة. 200-350 كلمة.",
  scopeUnderstanding: "فهم تفصيلي ودقيق لنطاق العمل وفق ما ورد في وثائق المناقصة: المخرجات، المواقع، الكميات، الشروط الفنية. 400-700 كلمة. استخدم نقاطاً عند الحاجة.",
  methodology: "منهجية تنفيذ مفصلة بمراحل واضحة (تخطيط، تنفيذ، اختبار، تسليم) مع أدوات ومعايير لكل مرحلة. 500-800 كلمة.",
  team: "هيكل الفريق المقترح بأدوار محددة (مدير المشروع، قائد فني، مهندسون، إلخ) ومؤهلاتهم وخبراتهم. 300-500 كلمة.",
  timeline: "جدول زمني تفصيلي بأسابيع/أشهر، مراحل، معالم رئيسية، وتسليمات لكل مرحلة. اعرض كقائمة منظمة.",
  qualityManagement: "خطة إدارة الجودة: معايير (ISO ذات الصلة)، نقاط فحص، مراجعات داخلية، مؤشرات أداء قابلة للقياس. 300-500 كلمة.",
  riskManagement: "تحديد وتقييم وتخفيف 5-8 مخاطر محتملة بشكل جدولي (المخاطرة | الاحتمالية | الأثر | إجراءات التخفيف).",
  conclusion: "خاتمة مقنعة موجزة تختصر القيمة المضافة وتأكيد الالتزام. 150-250 كلمة.",
  financialSummary: "ملخص مالي تنفيذي للعرض: الفئات الرئيسية للتكاليف، النسب التقريبية، الإجمالي قبل الضريبة وبعدها. 200-350 كلمة.",
  pricingTable: "جدول أسعار تفصيلي بصيغة Markdown قياسية | البند | الوصف | الكمية | الوحدة | سعر الوحدة (ر.س) | الإجمالي (ر.س) | ... استخدم أرقاماً واقعية مبنية على سياق السوق المعطى. اشمل في النهاية: الإجمالي قبل الضريبة، ضريبة القيمة المضافة 15%، الإجمالي مع الضريبة.",
  paymentTerms: "شروط دفع مفصلة: دفعة مقدمة، دفعات مرحلية مرتبطة بالمعالم، دفعة احتجاز. 200-300 كلمة.",
  financialGuarantees: "ضمانات مالية بالتفصيل: ضمان ابتدائي (نسبة ومدة)، ضمان حسن تنفيذ، ضمان صيانة. متوافق مع الأنظمة السعودية.",
  totalAmount: "إجمالي قيمة العقد كرقم واضح بالأرقام والحروف (ر.س)، مع تفصيل بسيط: شامل/غير شامل ضريبة القيمة المضافة 15%، صلاحية العرض.",
};
const SECTION_DESCRIPTIONS_EN: Record<string, string> = {
  introduction: "Professional introduction tailored to this specific tender: introduce the company, why this project, your value proposition. 200-350 words.",
  scopeUnderstanding: "Detailed scope understanding from the tender documents: deliverables, sites, quantities, technical conditions. 400-700 words. Use bullets where helpful.",
  methodology: "Detailed implementation methodology with clear phases (planning, execution, testing, delivery) and tools/standards per phase. 500-800 words.",
  team: "Proposed team structure with specific roles (PM, technical lead, engineers, etc.), qualifications and experience. 300-500 words.",
  timeline: "Detailed timeline in weeks/months, phases, key milestones, deliverables per phase. Present as a clean ordered list.",
  qualityManagement: "Quality plan: standards (relevant ISO), inspection points, internal reviews, measurable KPIs. 300-500 words.",
  riskManagement: "Identify, assess and mitigate 5-8 risks in tabular form (Risk | Probability | Impact | Mitigation).",
  conclusion: "Concise compelling closing that reinforces value and commitment. 150-250 words.",
  financialSummary: "Executive financial summary: main cost categories, approximate ratios, totals before/after VAT. 200-350 words.",
  pricingTable: "Detailed pricing as a standard Markdown table | Item | Description | Qty | Unit | Unit Price (SAR) | Total (SAR) | ... using realistic numbers grounded in the given market context. Append: subtotal, 15% VAT, grand total with VAT.",
  paymentTerms: "Detailed payment schedule: advance, milestone-linked progress payments, retention. 200-300 words.",
  financialGuarantees: "Financial guarantees in detail: bid bond (% and term), performance bond, maintenance bond. Saudi-regulation compliant.",
  totalAmount: "Total contract value as a clear figure in digits and words (SAR), with: inclusive/exclusive of 15% VAT, bid validity.",
};

export const PROPOSAL_SECTION_KEYS = Object.keys(SECTION_DESCRIPTIONS_AR);

/**
 * Regenerate a single proposal section, given full tender context, the existing
 * draft (for cross-section consistency), optional user instructions, and optional
 * pricing/market context (used for financial sections to ground numbers in reality).
 */
export async function generateProposalSection(args: {
  sectionKey: string;
  language: "ar" | "en";
  tenderText: string;
  tenderSummary?: string | null;
  analysis: Record<string, unknown>;
  companyProfile: string;
  currentDraft: Record<string, string | null | undefined>;
  instructions?: string;
  pricingContext?: string;
}): Promise<string> {
  const { client, model } = await getClient();
  const { sectionKey, language, tenderText, tenderSummary, analysis, companyProfile, currentDraft, instructions, pricingContext } = args;
  const isAr = language === "ar";
  const desc = (isAr ? SECTION_DESCRIPTIONS_AR : SECTION_DESCRIPTIONS_EN)[sectionKey];
  if (!desc) throw new Error(`Unknown section: ${sectionKey}`);

  // Build a compact draft summary so the model maintains consistency with other sections
  const otherSections = Object.entries(currentDraft)
    .filter(([k, v]) => k !== sectionKey && typeof v === "string" && v && v.trim().length > 0)
    .map(([k, v]) => `[${k}]: ${(v as string).slice(0, 350)}`)
    .join("\n");

  const systemPrompt = isAr
    ? `أنت كاتب عروض متخصص في المناقصات الحكومية السعودية. اكتب قسماً واحداً فقط من العرض بأعلى جودة احترافية، متماسكاً مع باقي أقسام العرض، وبصياغة جاهزة للتسليم دون مقدمات أو تعليقات وصفية. لا تضع عنواناً للقسم في النص.`
    : `You are a professional Saudi government tender proposal writer. Produce only ONE section of the proposal at top professional quality, internally consistent with the rest of the draft, ready for delivery, with no preamble, no labels, no meta commentary. Do not include the section title in the body.`;

  const userPrompt = `
${isAr ? "وصف القسم المطلوب:" : "Section spec:"} ${desc}

${isAr ? "ملف الشركة:" : "Company:"} ${companyProfile}

${tenderSummary ? `${isAr ? "ملخص الوثائق:" : "Tender brief:"}\n${tenderSummary.slice(0, 9000)}` : ""}

${isAr ? "نتائج التحليل:" : "Analysis:"} ${JSON.stringify(analysis).slice(0, 2500)}

${pricingContext ? `${isAr ? "سياق سوقي للأسعار (مرجعي فقط):" : "Market pricing context (reference only):"}\n${pricingContext.slice(0, 4000)}\n` : ""}

${otherSections ? `${isAr ? "ملخص الأقسام الأخرى للحفاظ على الاتساق:" : "Other sections (for consistency only):"}\n${otherSections.slice(0, 4000)}` : ""}

${instructions ? `${isAr ? "تعليمات خاصة من المستخدم (تجاوز فيها أي تعارض):" : "User instructions (take precedence):"}\n${instructions}` : ""}

${isAr ? "اكتب القسم الآن فقط، بنص خام (Markdown مسموح للجداول/القوائم)." : "Write the section now, plain text (Markdown allowed for tables/lists)."}`;

  const resp = await client.chat.completions.create({
    model,
    messages: [{ role: "system", content: systemPrompt }, { role: "user", content: userPrompt }],
    temperature: 0.45,
    max_tokens: 2200,
  });
  return (resp.choices[0]?.message?.content ?? "").trim();
}

/**
 * Build a market-pricing context string from prior proposals in the same company
 * and AI-derived reasonable Saudi market ranges. This grounds the financial section
 * in real numbers instead of hallucinated ones.
 */
export async function inferMarketPricing(args: {
  language: "ar" | "en";
  tenderTitle: string;
  tenderSummary?: string | null;
  pastProposals: Array<{ title: string; totalAmount?: string | null; pricingTable?: string | null }>;
  webContext?: string; // formatted Tavily results — see lib/marketSearch.ts
}): Promise<string> {
  const { client, model } = await getClient();
  const { language, tenderTitle, tenderSummary, pastProposals, webContext } = args;
  const isAr = language === "ar";

  const refs = pastProposals
    .filter((p) => p.totalAmount || p.pricingTable)
    .slice(0, 6)
    .map((p, i) => `(${i + 1}) ${p.title}\n${isAr ? "الإجمالي:" : "Total:"} ${p.totalAmount ?? "-"}\n${isAr ? "بنود:" : "Items:"} ${(p.pricingTable ?? "").slice(0, 600)}`)
    .join("\n\n");

  const webBlock = webContext
    ? (isAr
        ? `\n\nنتائج بحث ويب من مصادر سعودية شفافة (يجب أن تُستشهَد بالمصدر بجوار كل بند سعر):\n${webContext.slice(0, 5000)}`
        : `\n\nWeb-search results from transparent Saudi sources (cite the source URL next to each price):\n${webContext.slice(0, 5000)}`)
    : "";

  const prompt = isAr
    ? `أنت محلل أسعار متخصص في السوق السعودي. أعطِ ملخصاً واقعياً لمدى الأسعار المعقول لمناقصة بهذا الوصف، مستنداً إلى عروض الشركة السابقة، نتائج البحث الويبي السعودي، والأسعار السائدة. اذكر نطاقات سعرية رقمية بالريال السعودي حيثما أمكن، واستشهد بالمصدر (مناقصة سابقة أو رابط الويب) بجوار كل سعر.

${isAr ? "المناقصة الحالية:" : "Current tender:"} ${tenderTitle}
${tenderSummary ? `${isAr ? "الموجز:" : "Brief:"} ${tenderSummary.slice(0, 3000)}` : ""}

${isAr ? "عروض سابقة للشركة:" : "Prior proposals:"}
${refs || (isAr ? "(لا توجد عروض سابقة)" : "(none)")}${webBlock}`
    : `You are a Saudi-market pricing analyst. Provide a realistic price-range brief for a tender of this description, using the company's prior proposals, Saudi web-search results, and prevailing market norms. Give numeric SAR ranges where possible, and cite the source (prior proposal name or web URL) next to each price.

Current tender: ${tenderTitle}
${tenderSummary ? `Brief: ${tenderSummary.slice(0, 3000)}` : ""}

Prior proposals:
${refs || "(none)"}${webBlock}`;

  const resp = await client.chat.completions.create({
    model,
    messages: [{ role: "user", content: prompt }],
    temperature: 0.35,
    max_tokens: 1200,
  });
  return (resp.choices[0]?.message?.content ?? "").trim();
}

export async function generateProposalImage(
  prompt: string,
  model: "dall-e-3" | "gpt-image-1" | "imagen-3" | "imagen-3-fast" = "dall-e-3",
): Promise<string> {
  if (model === "imagen-3" || model === "imagen-3-fast") {
    return generateGeminiImage(prompt, model);
  }
  const { client } = await getClient();
  const params: Record<string, unknown> = {
    model,
    prompt: prompt.slice(0, 4000),
    n: 1,
    size: "1024x1024",
  };
  if (model === "dall-e-3") {
    params.response_format = "b64_json";
    params.quality = "standard";
  }
  const response = await (client.images.generate as any)(params);
  const b64 = response.data?.[0]?.b64_json as string | undefined;
  if (!b64) throw new Error("No image data returned from AI");
  return b64;
}

async function generateGeminiImage(prompt: string, model: "imagen-3" | "imagen-3-fast"): Promise<string> {
  const apiKey = process.env.GEMINI_API_KEY;
  if (!apiKey) throw new Error("GEMINI_API_KEY is not set");

  const modelId = model === "imagen-3" ? "imagen-3.0-generate-002" : "imagen-3.0-fast-generate-001";
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelId}:predict?key=${apiKey}`;

  const body = {
    instances: [{ prompt: prompt.slice(0, 4000) }],
    parameters: { sampleCount: 1, outputMimeType: "image/png" },
  };

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`Gemini Imagen API error ${res.status}: ${errText.slice(0, 300)}`);
  }

  const data = await res.json() as any;
  const b64 = data?.predictions?.[0]?.bytesBase64Encoded as string | undefined;
  if (!b64) throw new Error("No image data returned from Gemini Imagen");
  return b64;
}

export async function chatWithTender(
  messages: Array<{ role: "user" | "assistant"; content: string }>,
  tenderContext: string,
  language: "ar" | "en",
): Promise<string> {
  const { client, model } = await getClient();
  const systemPrompt = language === "ar"
    ? `أنت مساعد ذكي متخصص في تحليل المناقصات الحكومية السعودية. لديك المعلومات التالية عن المناقصة:\n\n${tenderContext.slice(0, 6000)}`
    : `You are an AI assistant specialized in Saudi government tender analysis. You have the following tender information:\n\n${tenderContext.slice(0, 6000)}`;

  const response = await client.chat.completions.create({
    model,
    messages: [
      { role: "system", content: systemPrompt },
      ...messages,
    ],
    temperature: 0.7,
    max_tokens: 1500,
  });

  return response.choices[0]?.message?.content ?? "";
}

/**
 * Generate all 5 financial proposal sections in a single AI call,
 * grounded in market pricing context.  Returns a Record keyed by
 * section name (camelCase) → markdown string.
 */
export async function generateAllFinancialSections(args: {
  language: "ar" | "en";
  tenderTitle: string;
  tenderSummary?: string | null;
  analysis: Record<string, unknown>;
  companyProfile: string;
  pricingContext: string;
  currentDraft?: Record<string, string | null | undefined>;
}): Promise<Record<string, string>> {
  const { client, model } = await getClient();
  const { language, tenderTitle, tenderSummary, analysis, companyProfile, pricingContext, currentDraft } = args;
  const isAr = language === "ar";

  const technicalSnippet = currentDraft
    ? ["introduction", "scopeUnderstanding", "methodology", "timeline"]
        .map((k) => currentDraft[k]?.slice(0, 300) || "")
        .filter(Boolean)
        .join("\n")
        .slice(0, 1200)
    : "";

  const system = isAr
    ? `أنت كاتب عروض متخصص في المناقصات الحكومية السعودية. مهمتك كتابة الأقسام المالية الخمسة لعرض واحد دفعة واحدة، مبنيةً على سياق أسعار سوقية واقعي. أعِد ناتجاً بصيغة JSON فقط.`
    : `You are a Saudi government tender proposal writer. Your task is to write all five financial sections of a proposal in one shot, grounded in real market-pricing context. Return JSON only.`;

  const sectionSpecs = isAr ? `
- financialSummary: ملخص مالي تنفيذي شامل: الفئات الرئيسية للتكاليف، النسب التقريبية، الإجمالي قبل الضريبة وبعدها. 200-350 كلمة.
- pricingTable: جدول أسعار تفصيلي بصيغة Markdown قياسية (| البند | الوصف | الكمية | الوحدة | سعر الوحدة ر.س | الإجمالي ر.س |). استخدم أرقاماً واقعية مستنداً لسياق السوق أدناه. اشمل في الأسفل: المجموع قبل الضريبة، ضريبة القيمة المضافة 15%، الإجمالي الكلي.
- paymentTerms: شروط دفع مفصلة: دفعة مقدمة، دفعات مرحلية مرتبطة بمعالم التسليم، دفعة احتجاز. 200-300 كلمة.
- financialGuarantees: ضمانات مالية: ضمان ابتدائي (نسبة ومدة)، ضمان حسن التنفيذ، ضمان الصيانة — متوافقة مع الأنظمة السعودية.
- totalAmount: إجمالي قيمة العقد بالأرقام والحروف (ر.س)، مع تحديد ما إذا كان شاملاً أو غير شامل ضريبة القيمة المضافة 15%، وصلاحية العرض.`
    : `
- financialSummary: Executive financial summary: main cost categories, approximate ratios, totals before/after VAT. 200-350 words.
- pricingTable: Detailed pricing as standard Markdown table (| Item | Description | Qty | Unit | Unit Price SAR | Total SAR |). Use realistic numbers grounded in the market context below. Append: subtotal, 15% VAT, grand total.
- paymentTerms: Detailed payment schedule: advance, milestone-linked progress payments, retention payment. 200-300 words.
- financialGuarantees: Financial guarantees: bid bond (% and term), performance bond, maintenance bond — Saudi-regulation compliant.
- totalAmount: Total contract value in digits and words (SAR), inclusive/exclusive of 15% VAT, bid validity.`;

  const user = `${isAr ? "المناقصة:" : "Tender:"} ${tenderTitle}
${tenderSummary ? `\n${isAr ? "الموجز:" : "Brief:"} ${tenderSummary.slice(0, 6000)}\n` : ""}
${isAr ? "ملف الشركة:" : "Company profile:"} ${companyProfile}

${isAr ? "نتائج التحليل:" : "Analysis:"} ${JSON.stringify(analysis).slice(0, 2000)}

${isAr ? "سياق الأسعار السوقية (مرجع إلزامي — استخدم الأرقام الواردة فيه):" : "Market pricing context (mandatory reference — use the figures herein):"}
${pricingContext.slice(0, 5000)}

${technicalSnippet ? `${isAr ? "مقتطف من الأقسام الفنية (للاتساق):" : "Technical sections snippet (for consistency):"}\n${technicalSnippet}\n` : ""}
${isAr ? "أعِد JSON يحتوي الحقول الخمسة التالية فقط بنصوص Markdown:" : "Return JSON with exactly these five fields as Markdown strings:"}
${sectionSpecs}`;

  const resp = await client.chat.completions.create({
    model,
    response_format: { type: "json_object" },
    messages: [
      { role: "system", content: system },
      { role: "user", content: user },
    ],
    temperature: 0.35,
    max_tokens: 4000,
  });

  const raw = resp.choices[0]?.message?.content ?? "{}";
  let parsed: any = {};
  try { parsed = JSON.parse(raw); } catch { parsed = {}; }
  const str = (v: any) => (typeof v === "string" ? v.trim() : "");
  return {
    financialSummary:    str(parsed.financialSummary),
    pricingTable:        str(parsed.pricingTable),
    paymentTerms:        str(parsed.paymentTerms),
    financialGuarantees: str(parsed.financialGuarantees),
    totalAmount:         str(parsed.totalAmount),
  };
}
